import useStore from "../../store.ts";
import type {EventMessage, EventPayloads} from "../IntegrationEventTypes.ts";
import {EventType} from "../IntegrationEventTypes.ts";
import {BUNDLE_VERSION} from "../../utils/BundleVersion.ts";

type UnlistenFn = () => void;
type Handler<T extends EventType> = (message: EventMessage<T>) => void;

export class ElectronManager {
    private initialized = false;
    private active = false;
    private unlistenFns: UnlistenFn[] = [];
    private handlers = new Map<EventType, Set<Handler<EventType>>>();
    private version: string | null = null;

    public async init(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;
        this.active = this.detectActive();

        if (!this.active) {
            console.info("[ElectronManager] Electron not detected; skipping init.");
            return;
        }

        const eventTypes = Object.values(EventType) as EventType[];
        for (const eventType of eventTypes) {
            const handler = (_event: unknown, payload: unknown) => {
                this.dispatch(eventType, payload);
            };
            window.electronAPI?.on(eventType, handler);
            this.unlistenFns.push(() => {
                window.electronAPI?.removeListener(eventType, handler);
            });
        }

        const stopAck = this.on(EventType.ReadyAck, (message) => {
            console.info("[ElectronManager] Init ack received", message.payload);
            this.version = message.payload.backendVersion ?? null;
            stopAck();
        });

        await this.emit(EventType.Ready, {version: BUNDLE_VERSION ?? undefined});
        await this.loadEvents();
    }

    public getVersion(): string | null {
        return this.version;
    }

    public async loadEvents(): Promise<void> {
        this.on(EventType.ScreenshotResponse, async (message) => {
            const store = useStore();
            let payload = message.payload;
            console.info(`[ElectronManager] Received capture response with id ${payload.requestId}.`);
            let img = new Uint8Array(payload.imageBase64);
            await store.socketManager.sendScreenDump(img, payload.deviceId);
        });
    }

    public isAvailable(): boolean {
        return this.active;
    }

    public on<T extends EventType>(type: T, handler: (message: EventMessage<T>) => void): () => void {
        const existing = this.handlers.get(type);
        const handlers = existing ?? new Set<Handler<EventType>>();
        handlers.add(handler as Handler<EventType>);
        if (!existing) {
            this.handlers.set(type, handlers);
        }
        return () => {
            handlers.delete(handler as Handler<EventType>);
        };
    }

    public async emit<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<void> {
        if (!this.active) return;
        window.electronAPI?.send(type, payload);
    }

    public async request<T extends EventType>(
        type: T,
        payload: EventPayloads[T]
    ): Promise<EventMessage<EventType> | null> {
        if (!this.active) return null;
        const message: EventMessage<T> = {type, payload};
        const response = await window.electronAPI?.invoke("device_request", {message});
        return (response as EventMessage<EventType>) ?? null;
    }

    public async dispose(): Promise<void> {
        this.unlistenFns.forEach((unlisten) => {
            try {
                unlisten();
            } catch (error) {
                console.warn("[ElectronManager] Failed to unlisten", error);
            }
        });
        this.unlistenFns = [];
        this.handlers.clear();
        this.active = false;
        this.initialized = false;
    }

    private detectActive(): boolean {
        return typeof window !== "undefined" && window.electronAPI != null;
    }

    private dispatch<T extends EventType>(type: T, payload: unknown): boolean {
        if (!this.isPayloadForType(type, payload)) {
            console.warn(`[ElectronManager] Invalid payload for ${type}`, payload);
            return false;
        }

        const handlers = this.handlers.get(type);
        if (!handlers || handlers.size === 0) {
            console.warn(`[ElectronManager] Unhandled event ${type}`, payload);
            return false;
        }

        const message: EventMessage<T> = {
            type,
            payload: payload as EventPayloads[T],
        };

        for (const handler of handlers) {
            try {
                handler(message as EventMessage<EventType>);
            } catch (error) {
                console.error(`[ElectronManager] Handler failed for ${type}`, error);
            }
        }

        return true;
    }

    private isPayloadForType<T extends EventType>(
        type: T,
        payload: unknown
    ): payload is EventPayloads[T] {
        switch (type) {
            case EventType.Ready:
                return this.isRecord(payload)
                    && (payload.version === undefined || typeof payload.version === "string");
            case EventType.ReadyAck:
                return this.isRecord(payload)
                    && (payload.frontendVersion === undefined || typeof payload.frontendVersion === "string")
                    && (payload.backendVersion === undefined || typeof payload.backendVersion === "string");
            case EventType.DeviceConnected:
            case EventType.DeviceDisconnected:
                return this.hasString(payload, "deviceId");
            case EventType.RequestStatus:
            case EventType.RebootRequest:
            case EventType.ScreenshotRequest:
                return this.hasString(payload, "requestId") && this.hasString(payload, "deviceId");
            case EventType.StatusResponse:
                return this.hasString(payload, "requestId")
                    && this.hasString(payload, "deviceId")
                    && this.isRecord(payload)
                    && this.isDeviceStatus(payload.status);
            case EventType.RebootResponse:
                return this.hasString(payload, "requestId")
                    && this.hasString(payload, "deviceId")
                    && this.hasBoolean(payload, "success");
            case EventType.ScreenshotResponse:
                return this.hasString(payload, "requestId")
                    && this.hasString(payload, "deviceId");
            case EventType.ScreenshotPolicy:
                return this.hasNumber(payload, "interval")
                    && this.hasNumber(payload, "retention")
                    && this.isRecord(payload)
                    && this.isTimeOfDay(payload.offloadTimestamp);
            case EventType.AuditSend:
                return this.hasString(payload, "requestId") && this.hasString(payload, "deviceId");
            case EventType.AuditResponse:
                return this.hasString(payload, "requestId")
                    && this.hasString(payload, "deviceId")
                    && this.hasBoolean(payload, "success")
                    && this.hasNumber(payload, "uploaded");
            case EventType.Error:
                if (!this.isRecord(payload)) return false;
                if (payload.requestId !== undefined && typeof payload.requestId !== "string") return false;
                return typeof payload.code === "string" && typeof payload.message === "string";
            case EventType.UpdaterManifestUrlRequest:
                return this.hasString(payload, "requestId");
            case EventType.UpdaterManifestUrlResponse:
                if (!this.isRecord(payload)) return false;
                if (payload.requestId !== undefined && typeof payload.requestId !== "string") return false;
                return payload.manifestUrl === null || typeof payload.manifestUrl === "string";
            default:
                return false;
        }
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === "object" && value !== null && !Array.isArray(value);
    }

    private hasString(value: unknown, key: string): boolean {
        return this.isRecord(value) && typeof value[key] === "string";
    }

    private hasBoolean(value: unknown, key: string): boolean {
        return this.isRecord(value) && typeof value[key] === "boolean";
    }

    private isDeviceStatus(value: unknown): boolean {
        return value === "online" || value === "offline";
    }

    private hasNumber(value: unknown, key: string): boolean {
        return this.isRecord(value) && typeof value[key] === "number" && Number.isFinite(value[key]);
    }

    private isTimeOfDay(value: unknown): boolean {
        return this.hasNumber(value, "hour")
            && this.hasNumber(value, "minute")
            && this.hasNumber(value, "second");
    }
}

export const electronManager = new ElectronManager();
